// useSubheaderScroll — the subheader's scroll machine (ES-13522).
//
//   pinned — within the dock zone at the very top: the docked dark bar.
//   pill   — scrolled past the dock zone: the detached glass pill, sticky.
//   hidden — scrolled well past it AND still moving down: the pill slides out of
//            frame. Any upward scroll brings it back (-> pill).
//
// Feel: scroll down a little and the docked bar smoothly morphs into the pill
// and stays; keep scrolling down and the pill slides away; scroll up and it
// returns; scroll to the top and it re-docks. The state is frozen while a
// menu/popover is open.
//
// Hide/show past the dock zone is driven by scroll DIRECTION + ACCUMULATED
// DISTANCE, not by per-frame velocity. We sum how far the user has travelled in
// the current direction and only flip once that sustained travel crosses a
// threshold (hide after ~20px down, reveal after ~10px up — deliberately easier
// to reveal than to hide). The accumulator RESETS the instant the direction
// reverses, so a down/up/down jitter never sums into a trigger and the pill
// can't flicker. This behaves consistently across mouse wheel, trackpad and
// touch, where raw velocity varies wildly. (See Headroom.js / NN/g partially
// persistent headers.)
//
// The listener is registered in the CAPTURE phase on window so it fires no
// matter which element actually scrolls — the app scrolls a nested container on
// some pages, not the window — and the scroll offset is read from the event's
// target.
import { useEffect, useRef, useState } from "react";

// Reveal hold: a jump-to result's own smooth scroll (often upward) would
// otherwise reveal the pill. While held, the pill stays hidden below the dock
// zone. Scrolls extend the hold; real user input (wheel/touch/key) ends it.
const HOLD_EXTEND_MS = 250;
let revealHeldUntil = 0;

export const holdSubheaderReveal = (ms = 1500) => {
  revealHeldUntil = Date.now() + ms;
};

const isRevealHeld = () => Date.now() < revealHeldUntil;

const releaseRevealHold = () => {
  revealHeldUntil = 0;
};

// Freeze: ignore scroll movement (like `locked`) for `ms` (top-nav search).
let frozenUntil = 0;

export const freezeSubheaderScroll = (ms) => {
  frozenUntil = Date.now() + ms;
};

// Reads the current scroll offset regardless of which element scrolls: the app
// scrolls <body> (body { height:100vh; overflow:auto }) on some pages, the
// window on others, and a nested container elsewhere. Take the max of the root
// scroll sources plus the event target's own scrollTop when it's an element.
const scrollOffset = (target) => {
  const rootY = Math.max(
    window.scrollY || 0,
    document.documentElement.scrollTop || 0,
    document.body.scrollTop || 0,
  );
  const el =
    target &&
    target.nodeType === 1 &&
    target !== document.body &&
    target !== document.documentElement &&
    typeof target.scrollTop === "number"
      ? target.scrollTop
      : 0;
  return Math.max(rootY, el);
};

export const useSubheaderScroll = ({
  // Hysteresis around the dock<->pill boundary. Entering the pill happens at a
  // low threshold (starts the morph early), but returning to the docked bar only
  // happens near the very top. The gap between the two is a dead-band: small
  // scroll jitter / momentum settling in between can't flip the state, so the
  // bar never glitches between pinned and pill when you pause mid-transition.
  pillOn = 48, // scrolled past this -> morph to the pill
  pillOff = 8, // dropped back below this -> re-dock to the pinned bar
  hideZone = 240, // pill always shown up to here; beyond, direction decides
  hideDistance = 20, // sustained downward travel (px) past the band to hide
  revealDistance = 10, // sustained upward travel (px) to reveal (easier to show)
  locked = false,
} = {}) => {
  const [state, setState] = useState("pinned");
  const lastY = useRef(0);
  const stateRef = useRef("pinned");
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let ticking = false;
    let pendingY = 0;
    // Distance travelled in the current direction: > 0 net down, < 0 net up.
    // Reset to the latest step whenever the direction reverses.
    let accum = 0;

    const compute = () => {
      ticking = false;
      const y = pendingY;

      if (lockedRef.current || Date.now() < frozenUntil) {
        lastY.current = y;
        return;
      }

      const cur = stateRef.current;
      const diff = y - lastY.current;
      lastY.current = y;

      // Accumulate travel in the current direction; a reversal starts the count
      // over from this step so opposite jitter can never build toward a trigger.
      if (diff > 0) {
        accum = accum > 0 ? accum + diff : diff;
      } else if (diff < 0) {
        accum = accum < 0 ? accum + diff : diff;
      }

      let next;
      if (cur === "pinned") {
        // Only leave the docked bar once we've clearly scrolled past pillOn.
        next = y > pillOn ? "pill" : "pinned";
        accum = 0;
      } else if (y <= pillOff) {
        // Back near the top -> re-dock.
        next = "pinned";
        accum = 0;
      } else if (y <= hideZone) {
        // In the always-shown band: position decides, distance irrelevant.
        next = "pill";
        accum = 0;
      } else if (accum <= -revealDistance) {
        // Sustained upward travel -> reveal the pill.
        next = "pill";
      } else if (accum >= hideDistance) {
        // Sustained downward travel -> hide the pill.
        next = "hidden";
      } else {
        // Not enough sustained travel in either direction yet -> hold.
        next = cur;
      }

      // Jump-to scroll in flight: keep the pill hidden (re-docking allowed).
      if (next === "pill" && cur !== "pill" && y > pillOff && isRevealHeld()) {
        next = "hidden";
        accum = 0;
      }

      if (next !== cur) {
        stateRef.current = next;
        setState(next);
      }
    };

    const onScroll = (e) => {
      const t = e && e.target;
      // Ignore scrolls that happen inside the top nav / its dropdown panels,
      // any modal overlay (the mega-menu, favorites list, or the mobile vehicle
      // nav overlay scrolling their own overflow), a listbox — every
      // react-select menu list carries role="listbox", and a portalled one sits
      // on <body>, outside both of the above — or anything marked
      // data-subheader-scroll-ignore (the desktop VP sidebar). The capture
      // listener would otherwise read those containers' scrollTop as page
      // movement and hide/show the pill.
      if (
        t &&
        t.nodeType === 1 &&
        t.closest &&
        (t.closest(".topnav") ||
          t.closest('[aria-modal="true"]') ||
          t.closest('[role="listbox"]') ||
          t.closest("[data-subheader-scroll-ignore]") ||
          t.closest(".disclaimer-overlay"))
      ) {
        return;
      }
      pendingY = scrollOffset(t);
      if (isRevealHeld()) {
        revealHeldUntil = Math.max(
          revealHeldUntil,
          Date.now() + HOLD_EXTEND_MS,
        );
      }
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(compute);
    };

    // Capture phase: scroll doesn't bubble, but capture still fires for scrolls
    // on any descendant scroll container.
    window.addEventListener("scroll", onScroll, {
      passive: true,
      capture: true,
    });
    // Real user input ends a reveal hold.
    const userInputs = ["wheel", "touchstart", "keydown"];
    userInputs.forEach((type) =>
      window.addEventListener(type, releaseRevealHold, {
        passive: true,
        capture: true,
      }),
    );
    // Prime from the document in case we mount already scrolled.
    pendingY = scrollOffset(document);
    compute();

    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      userInputs.forEach((type) =>
        window.removeEventListener(type, releaseRevealHold, { capture: true }),
      );
    };
  }, [pillOn, pillOff, hideZone, hideDistance, revealDistance]);

  return state;
};
